import { useRef, useState } from 'react';

import { ReviewUnmatchedProductsRequest } from '../../../../types/UnmatchedVesselProductMessage';
import { VesselMessage } from '../../../../types/VesselMessage';
import { VesselProductMessage } from '../../../../types/VesselProductMessage';
import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { NoFilterResults } from '../../../cdl/Filter/NoFilterResults/NoFilterResults';
import { StatePlaceholder } from '../../../cdl/StatePlaceholder/StatePlaceholder';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { DeleteConfirmPopup } from '../../../common/DeleteConfirmPopup/DeleteConfirmPopup';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { useRole } from '../../../common/hooks/useRole';
import { useUnmatchedVesselProducts } from '../../../common/hooks/useUnmatchedVesselProducts';
import { useVesselProducts } from '../../../common/hooks/useVesselProducts';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { IconPackage } from '../../../common/icons/cdl/Package';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { useDeleteVesselProduct } from '../hooks/useDeleteVesselProduct';
import type { LubricationChartUpload } from '../hooks/useLubricationChartUpload';
import { useReviewUnmatchedProducts } from '../hooks/useReviewUnmatchedProducts';

import { AddVesselProductModal } from './AddVesselProductModal';
import { EditVesselProductModal } from './EditVesselProductModal';
import { HiddenFileInput } from './HiddenFileInput';
import { LubricationChartDropZone } from './LubricationChartDropZone';
import { ProcessingVesselProductsTable } from './ProcessingVesselProductsTable';
import { ReplaceLubricationChartModal } from './ReplaceLubricationChartModal';
import { ReviewUnmatchedProductsModal } from './ReviewUnmatchedProductsModal';
import { UnmatchedProductsCallout } from './UnmatchedProductsCallout';
import { VesselProductRowActionsDropdown } from './VesselProductRowActionsDropdown';
import { VesselProductsActionsDropdown } from './VesselProductsActionsDropdown';
import { VesselProductsFilters } from './VesselProductsFilters';

interface VesselProductsTableProps {
    vessel: VesselMessage;
    lubricationChartUpload: LubricationChartUpload;
}

export const VesselProductsTable = ({ vessel, lubricationChartUpload }: VesselProductsTableProps) => {
    const { getHumanReadableValue } = useEnums();
    const role = useRole();
    const { addToast, addErrorToast } = useToasts();

    const { isProcessing, uploadLubricationChart } = lubricationChartUpload;
    const uploadTriggerRef = useRef<(() => void) | null>(null);

    const handleFileUpload = async (file: File) => {
        try {
            await uploadLubricationChart(file);
        } catch {
            return;
        }
    };

    const triggerUpload = () => {
        uploadTriggerRef.current?.();
    };

    const setUploadTrigger = (trigger: () => void) => {
        uploadTriggerRef.current = trigger;
    };

    const vesselProductsQuery = useVesselProducts(vessel.id);

    const unmatchedProductsQuery = useUnmatchedVesselProducts(vessel.id);
    const reviewMutation = useReviewUnmatchedProducts(vessel.id);
    const [showReviewModal, setShowReviewModal] = useState(false);

    const pendingUnmatchedProducts = unmatchedProductsQuery.data?.unmatchedProducts ?? [];

    const handleReviewSubmit = async (request: ReviewUnmatchedProductsRequest) => {
        try {
            await reviewMutation.mutateAsync(request);
            if (request.resolutions.length > 0) {
                addToast(translate('vessel.products.unmatchedModal.toasts.success'));
            } else {
                addToast(translate('vessel.products.unmatchedModal.toasts.allReviewed'));
            }
        } catch (error) {
            addErrorToast(translate('vessel.products.unmatchedModal.toasts.error'));
            throw error;
        }
    };

    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const handleFilesDrop = (files: File[]) => {
        if (files.length > 0) {
            handleFileSelection(files[0]);
        }
    };

    const handleFileSelection = (file: File) => {
        if (vesselProductsQuery.data?.products && vesselProductsQuery.data.products.length > 0) {
            setPendingFile(file);
            setShowReplaceModal(true);
        } else {
            handleFileUpload(file);
        }
    };

    const handleConfirmReplace = async () => {
        setShowReplaceModal(false);
        if (pendingFile) {
            await handleFileUpload(pendingFile);
            setPendingFile(null);
        }
    };

    const handleCancelReplace = () => {
        setShowReplaceModal(false);
        setPendingFile(null);
    };

    const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
    const [productToDelete, setProductToDelete] = useState<VesselProductMessage | null>(null);
    const [productToEdit, setProductToEdit] = useState<VesselProductMessage | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const deleteVesselProductMutation = useDeleteVesselProduct(vessel.id);

    if (isProcessing) {
        return (
            <div>
                <Box display="flex" justifyContent="space-between" marginBottom={16}>
                    <Text variant="title" as="h2">
                        {translate('vessel.products.headline')}
                    </Text>
                </Box>

                <ProcessingVesselProductsTable />
            </div>
        );
    }

    if (vesselProductsQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (vesselProductsQuery.isError) {
        return <AdvancedError />;
    }

    if (!vesselProductsQuery.data.products.length) {
        const emptyStateContent = (
            <Box display="flex" justifyContent="center" marginTop={64}>
                <StatePlaceholder
                    title={translate('vessel.products.emptyState')}
                    description={translate('vessel.products.emptyState.description')}
                    Icon={IconPackage}
                    action={
                        role.hasAnyAdminRights() ? (
                            <Box display="grid" rowGap={24}>
                                <Callout
                                    description={translate('vessel.products.emptyState.disclaimer')}
                                    Icon={IconInfoCircle}
                                />
                                <ButtonGroup>
                                    <Button
                                        onClick={triggerUpload}
                                        disabled={isProcessing}
                                        LeadingIcon={IconPlus}
                                        emphasis="high"
                                    >
                                        {translate('vessel.products.uploadButton')}
                                    </Button>

                                    <Button onClick={() => setShowAddModal(true)} LeadingIcon={IconPlus}>
                                        {translate('vessel.products.addProducts')}
                                    </Button>
                                </ButtonGroup>
                            </Box>
                        ) : null
                    }
                />
            </Box>
        );

        return (
            <div>
                {role.hasAnyAdminRights() ? (
                    <LubricationChartDropZone onFilesDrop={handleFilesDrop}>
                        {emptyStateContent}
                    </LubricationChartDropZone>
                ) : (
                    emptyStateContent
                )}
                <HiddenFileInput
                    accept="application/pdf,image/*"
                    onChange={handleFileSelection}
                    onTrigger={setUploadTrigger}
                />
                <AddVesselProductModal
                    isOpen={showAddModal}
                    onDismiss={() => setShowAddModal(false)}
                    vesselId={vessel.id}
                />
            </div>
        );
    }

    const hasManualAndImportedProducts =
        vesselProductsQuery.data.products.some((product) => !product.imported) &&
        vesselProductsQuery.data.products.some((product) => product.imported);

    return (
        <div>
            <Box display="flex" justifyContent="space-between" marginBottom={16}>
                <Text variant="title" as="h2">
                    {translate('vessel.products.headline')}
                </Text>

                {role.hasAnyAdminRights() ? (
                    <ButtonGroup>
                        <Button onClick={() => setShowAddModal(true)} LeadingIcon={IconPlus}>
                            {translate('vessel.products.addProducts')}
                        </Button>
                        <VesselProductsActionsDropdown
                            vesselId={vessel.id}
                            onUploadClick={triggerUpload}
                            productIds={vesselProductsQuery.data.products.map((product) => product.id)}
                        />
                    </ButtonGroup>
                ) : null}
            </Box>
            {hasManualAndImportedProducts ? (
                <Box marginBottom={16}>
                    <Callout description={translate('vessel.products.manualEditsCallout')} Icon={IconInfoCircle} />
                </Box>
            ) : null}
            {pendingUnmatchedProducts.length > 0 && role.hasAnyAdminRights() ? (
                <Box marginBottom={16}>
                    <UnmatchedProductsCallout
                        count={pendingUnmatchedProducts.length}
                        onReview={() => setShowReviewModal(true)}
                    />
                </Box>
            ) : null}
            <VesselProductsFilters allEntries={vesselProductsQuery.data.products}>
                {(filtered) => (
                    <>
                        {filtered.length === 0 ? (
                            <NoFilterResults />
                        ) : (
                            <TableBuilder data={filtered} grow={false}>
                                <TableBuilderColumn<VesselProductMessage>
                                    header={translate('vessel.products.productName')}
                                    width="25%"
                                >
                                    {(vesselProduct) => (
                                        <Box display="flex" alignItems="center" gap={8}>
                                            <Text>{vesselProduct.product.name}</Text>
                                            {vesselProduct.product.archived ? (
                                                <Tooltip
                                                    label={translate('vessel.products.archivedTooltip')}
                                                    showArrow={true}
                                                    maxWidth="160px"
                                                >
                                                    <span>
                                                        <Tag variant="attention">
                                                            {translate('vessel.products.archived')}
                                                        </Tag>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            {vesselProduct.isInTank ? (
                                                <Tag variant="accent">{translate('vessel.products.inTank')}</Tag>
                                            ) : null}
                                            {hasManualAndImportedProducts && !vesselProduct.imported ? (
                                                <Tag variant="default">{translate('vessel.products.custom')}</Tag>
                                            ) : null}
                                        </Box>
                                    )}
                                </TableBuilderColumn>
                                <TableBuilderColumn<VesselProductMessage>
                                    header={translate('vessel.products.supplierName')}
                                    width="10%"
                                    truncate
                                >
                                    {(vesselProduct) =>
                                        getHumanReadableValue(
                                            'supplierProductGroup',
                                            vesselProduct.product.supplierProductGroup
                                        )
                                    }
                                </TableBuilderColumn>
                                <TableBuilderColumn<VesselProductMessage>
                                    header={translate('vessel.products.productGroup')}
                                    width="15%"
                                    truncate
                                >
                                    {(vesselProduct) =>
                                        getHumanReadableValue('productGroup', vesselProduct.product.group)
                                    }
                                </TableBuilderColumn>
                                <TableBuilderColumn<VesselProductMessage>
                                    header={translate('vessel.products.dateCreated')}
                                    width="10%"
                                >
                                    {(vesselProduct) => formatDate({ date: vesselProduct.dateCreated })}
                                </TableBuilderColumn>
                                <TableBuilderColumn<VesselProductMessage>
                                    header={translate('vessel.products.dateUpdated')}
                                    width="10%"
                                >
                                    {(vesselProduct) => formatDate({ date: vesselProduct.dateUpdated })}
                                </TableBuilderColumn>
                                <TableBuilderColumn<VesselProductMessage>
                                    header={translate('vessel.products.note')}
                                    width="20%"
                                    truncate
                                >
                                    {(vesselProduct) => vesselProduct.note}
                                </TableBuilderColumn>

                                {role.hasAnyAdminRights() ? (
                                    <TableBuilderColumn<VesselProductMessage> header="" width="64px">
                                        {(vesselProduct) => (
                                            <Box display="flex" justifyContent="flex-end">
                                                <VesselProductRowActionsDropdown
                                                    vesselProduct={vesselProduct}
                                                    onEdit={() => {
                                                        setProductToEdit(vesselProduct);
                                                    }}
                                                    onRemove={() => {
                                                        setProductToDelete(vesselProduct);
                                                        setShowDeleteConfirmPopup(true);
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </TableBuilderColumn>
                                ) : null}
                            </TableBuilder>
                        )}
                    </>
                )}
            </VesselProductsFilters>
            <HiddenFileInput
                accept="application/pdf,image/*"
                onChange={handleFileSelection}
                onTrigger={setUploadTrigger}
            />

            {productToEdit ? (
                <EditVesselProductModal
                    isOpen={!!productToEdit}
                    onDismiss={() => setProductToEdit(null)}
                    vesselId={vessel.id}
                    vesselProduct={productToEdit}
                    excludedProductIds={vesselProductsQuery.data.products.map(
                        (vesselProduct) => vesselProduct.productId
                    )}
                />
            ) : null}

            <AddVesselProductModal
                isOpen={showAddModal}
                onDismiss={() => setShowAddModal(false)}
                vesselId={vessel.id}
                excludedProductIds={vesselProductsQuery.data.products.map((vesselProduct) => vesselProduct.productId)}
            />

            {productToDelete ? (
                <DeleteConfirmPopup
                    isOpen={showDeleteConfirmPopup}
                    onDismiss={() => setShowDeleteConfirmPopup(false)}
                    onSubmit={async () => {
                        try {
                            await deleteVesselProductMutation.mutateAsync(productToDelete.id);
                            setShowDeleteConfirmPopup(false);
                            setProductToDelete(null);
                        } catch {
                            setShowDeleteConfirmPopup(false);
                            setProductToDelete(null);
                            addErrorToast(translate('vessel.products.toasts.deleteError'));
                        }
                    }}
                    headline={translate('vessel.products.deleteConfirm.headline')}
                    text={translate('vessel.products.deleteConfirm.text')}
                    itemToDelete={productToDelete.product.name}
                    deleteButtonText={translate('vessel.products.actions.remove')}
                    isSubmitting={deleteVesselProductMutation.isPending}
                />
            ) : null}

            <ReplaceLubricationChartModal
                isOpen={showReplaceModal}
                onDismiss={handleCancelReplace}
                onConfirm={handleConfirmReplace}
            />

            {pendingUnmatchedProducts.length > 0 ? (
                <ReviewUnmatchedProductsModal
                    isOpen={showReviewModal}
                    onDismiss={() => setShowReviewModal(false)}
                    unmatchedProducts={pendingUnmatchedProducts}
                    vesselId={vessel.id}
                    onSubmit={handleReviewSubmit}
                    isLoading={reviewMutation.isPending}
                />
            ) : null}
        </div>
    );
};
